import React, { useEffect, useState } from 'react';
import BlogFigureControls from './BlogFigureControls';

interface QuickstartStep { shortName: string; title: string; commands: string[]; description: string; output: string }

const STEPS: QuickstartStep[] = [
  {
    shortName: 'Start Bridge',
    title: 'Start the bridge',
    commands: ['harnessgg-blender bridge start'],
    description: 'Launches the local bridge that routes commands to Blender.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "bridge.start",
  "data": {
    "status": "started",
    "pid": 21696,
    "host": "127.0.0.1",
    "port": 41749,
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Verify Install',
    title: 'Verify the install',
    commands: ['harnessgg-blender doctor'],
    description: 'Checks that the bridge and Blender are ready.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "doctor",
  "data": {
    "healthy": true,
    "checks": [
      { "name": "blender.binary", "ok": true, "value": "Blender 5.0.1" }
    ],
    "warnings": []
  }
}`,
  },
  {
    shortName: 'New Project',
    title: 'Create a project',
    commands: ['harnessgg-blender file new scene.blend --overwrite'],
    description: 'Creates a fresh .blend project file.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "file.new",
  "data": {
    "ok": true,
    "output": "scene.blend",
    "changed": true,
    "idempotent": false,
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Add Object',
    title: 'Add an object',
    commands: ['harnessgg-blender object add scene.blend CUBE --name CubeA'],
    description: 'Adds a named cube mesh to the scene.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "object.add",
  "data": {
    "ok": true,
    "object": "CubeA",
    "output": "scene.blend",
    "changed": true,
    "idempotent": false,
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Material',
    title: 'Create a material',
    commands: ['harnessgg-blender material create scene.blend RedMetal --base-color "#C0392B" --metallic 0.8 --roughness 0.3'],
    description: 'Defines a PBR material with explicit parameters.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "material.create",
  "data": {
    "ok": true,
    "material": "RedMetal",
    "output": "scene.blend",
    "changed": true,
    "idempotent": false,
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Assign It',
    title: 'Assign it',
    commands: ['harnessgg-blender material assign scene.blend CubeA RedMetal'],
    description: 'Assigns the material to the named cube.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "material.assign",
  "data": {
    "ok": true,
    "object": "CubeA",
    "material": "RedMetal",
    "output": "scene.blend",
    "changed": true,
    "idempotent": false,
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Camera',
    title: 'Add a camera',
    commands: [
      'harnessgg-blender camera add scene.blend --name CamMain',
      'harnessgg-blender camera set-active scene.blend CamMain',
    ],
    description: 'Adds and activates a camera.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "camera.set-active",
  "data": {
    "ok": true,
    "activeCamera": "CamMain",
    "output": "scene.blend",
    "changed": true,
    "idempotent": false,
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Light',
    title: 'Add a light',
    commands: ['harnessgg-blender light add scene.blend SUN --name KeyLight --energy 3'],
    description: 'Adds a sun light with fixed energy.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "light.add",
  "data": {
    "ok": true,
    "light": "KeyLight",
    "type": "SUN",
    "energy": 3.0,
    "color": [1.0, 1.0, 1.0],
    "output": "scene.blend",
    "changed": true,
    "idempotent": false,
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Inspect',
    title: 'Inspect the scene',
    commands: ['harnessgg-blender file inspect scene.blend'],
    description: 'Reads the scene back as structured data.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "file.inspect",
  "data": {
    "ok": true,
    "project": "scene.blend",
    "scene": "Scene",
    "counts": {
      "objects": 3,
      "meshes": 1,
      "materials": 1,
      "cameras": 1,
      "lights": 1
    },
    "objects": [
      { "name": "CamMain", "type": "CAMERA" },
      { "name": "CubeA", "type": "MESH" },
      { "name": "KeyLight", "type": "LIGHT" }
    ],
    "warnings": []
  }
}`,
  },
  {
    shortName: 'Render',
    title: 'Render',
    commands: ['harnessgg-blender render still scene.blend out.png'],
    description: 'Renders through the active camera to a PNG.',
    output: `{
  "ok": true,
  "protocolVersion": "1.0",
  "command": "render.still",
  "data": {
    "ok": true,
    "outputImage": "out.png",
    "changed": true,
    "idempotent": false,
    "warnings": []
  }
}`,
  },
];
const BlenderQuickstartFigure: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [running, setRunning] = useState(false);
  const [outputVisible, setOutputVisible] = useState(true);
  useEffect(() => {
    setOutputVisible(false);
    const timeout = window.setTimeout(() => setOutputVisible(true), 80);
    return () => window.clearTimeout(timeout);
  }, [current]);

  useEffect(() => {
    if (!running) return;
    const interval = window.setInterval(() => {
      setCurrent((previous) => {
        if (previous === STEPS.length - 1) {
          setRunning(false);
          return previous;
        }
        return previous + 1;
      });
    }, 1800);
    return () => window.clearInterval(interval);
  }, [running]);

  const goTo = (index: number) => {
    setCurrent(index);
  };
  const togglePlayback = () => {
    if (running) {
      setRunning(false);
      return;
    }

    if (current === STEPS.length - 1) {
      goTo(0);
    }
    setRunning(true);
  };
  const step = STEPS[current];
  return (
    <figure className="blender-quickstart-breakout" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
      <div className="blog-figure-frame" style={{ overflow: 'hidden' }}>
          <section style={{ minWidth: 0, padding: 20 }}>
            <div className="blender-quickstart__terminal-scroll" style={{ boxSizing: 'border-box', display: 'flex', height: 48, alignItems: 'center', background: '#0c0a09', borderRadius: 8, padding: '10px 12px', marginBottom: 12, overflowX: 'auto', overflowY: 'hidden' }}>
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12.5, lineHeight: 1.7, whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#8baaad', marginRight: 8 }}>$</span>
                  <span style={{ color: '#f4fff8' }}>{step.commands.join(' && ')}</span>
                </div>
            </div>

            <div style={{ background: '#0c0a09', borderRadius: 8, padding: 8 }}>
              <pre className="blender-quickstart__terminal-scroll" aria-live="polite" style={{
                boxSizing: 'border-box', height: 260, margin: 0, padding: '6px 14px 12px', overflow: 'auto',
                border: '1px solid #1f2937', borderRadius: 6, background: '#111827', color: '#a8a29e',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12, lineHeight: 1.55,
                opacity: outputVisible ? 1 : 0, transform: outputVisible ? 'translateY(0)' : 'translateY(2px)',
                transition: 'opacity 160ms ease, transform 160ms ease',
              }}>
                <code>{step.output}</code>
              </pre>
            </div>

            <div className="blender-quickstart__step" aria-live="polite" style={{ boxSizing: 'border-box', display: 'flex', height: 50, alignItems: 'center', marginTop: 14, padding: '10px 12px', border: '1px solid #e7e5e4', borderRadius: 7, background: '#f5f5f4', overflowX: 'auto' }}>
              <p style={{ margin: 0, color: '#57534e', fontSize: 13, lineHeight: 1.45, whiteSpace: 'nowrap' }}>
                <strong style={{ color: '#1c3738', fontWeight: 600 }}>
                  {current + 1}. {step.shortName}
                </strong>
                {' - '}
                {step.description}
              </p>
            </div>

            <div style={{ marginTop: 12, color: '#78716c', fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12, textAlign: 'center' }}>
              {current + 1} / {STEPS.length}
            </div>

            <BlogFigureControls
              className="blender-quickstart__controls"
              playing={running}
              onPrevious={() => goTo(current - 1)}
              onToggle={togglePlayback}
              onNext={() => goTo(current + 1)}
              previousDisabled={current === 0}
              nextDisabled={current === STEPS.length - 1}
              label="Quickstart playback controls"
            />

          </section>
      </div>
      <figcaption className="blender-quickstart__caption">
        Harness commands for creating and rendering a minimal Blender output.
      </figcaption>
    </figure>
  );
};

export default BlenderQuickstartFigure;
